/**
 * `richText()` — the ONLY raw-HTML sink in the framework (RFC §3b, NORMATIVE).
 *
 * Sanitizes untrusted HTML (typical source: LLM-authored markdown rendered to
 * HTML) with a strict allowlist and returns a TemplateResult wrapping the
 * sanitized fragment. The AX auditor (later slice) flags any other `innerHTML`
 * write it observes. The footgun is contained to this one audited function,
 * not removed — that honesty is the contract.
 *
 * ── The allowlist (PINNED — changing it is an RFC §3b amendment) ──
 * Tags kept:        p, br, strong, em, code, pre, ul, ol, li, a, h1, h2, h3,
 *                   h4, blockquote
 * Attributes kept:  href on <a> ONLY, and only when it parses as an ABSOLUTE
 *                   http:// or https:// URL (everything else — javascript:,
 *                   data:, vbscript:, relative, malformed — is stripped).
 * Dropped WITH contents: script, style, iframe, frame, object, embed, link,
 *                   meta, base, title, head, noscript, template, textarea,
 *                   select, option, svg, math
 * Any other tag:    UNWRAPPED — the element is removed, its (sanitized)
 *                   children are kept in place (so <span>/<div> soup degrades
 *                   to its text; void elements like <img> vanish entirely).
 * Always removed:   comments, processing instructions, doctypes, and EVERY
 *                   attribute not listed above (all on* handlers, style,
 *                   class, src, srcset, …).
 *
 * Dependency-free; browser-runtime (parses via an inert <template>, which never
 * executes scripts). In dev, each call logs its call site + an FNV-1a content
 * hash so raw-HTML sinks stay observable.
 */

import { html, type TemplateResult } from "./html";

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "em",
  "code",
  "pre",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
]);

/** Tags whose CONTENT is code/metadata, never prose — dropped entirely. */
const DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "iframe",
  "frame",
  "object",
  "embed",
  "link",
  "meta",
  "base",
  "title",
  "head",
  "noscript",
  "template",
  "textarea",
  "select",
  "option",
  "svg",
  "math",
]);

const DEV =
  typeof process !== "undefined" &&
  process.env &&
  process.env["NODE_ENV"] !== "production";

/** FNV-1a 32-bit, hex — a cheap stable content fingerprint for dev logs. */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function isSafeHref(href: string): boolean {
  try {
    const { protocol } = new URL(href); // throws on relative/malformed — rejected
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function sanitizeElement(el: Element): void {
  const tag = el.localName;

  if (DROP_WITH_CONTENT.has(tag)) {
    el.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    // Unwrap: keep (and then sanitize) the children, drop the wrapper.
    const parent = el.parentNode;
    if (!parent) {
      el.remove();
      return;
    }
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    el.remove();
    return; // the caller's walk revisits the promoted children
  }

  // Allowed tag: strip every attribute except a safe href on <a>.
  for (const name of [...el.getAttributeNames()]) {
    if (tag === "a" && name === "href" && isSafeHref(el.getAttribute(name) ?? "")) {
      continue;
    }
    el.removeAttribute(name);
  }
}

function sanitizeChildren(node: Node): void {
  // Snapshot-free walk: children mutate under us (unwrap/remove), so iterate
  // until a pass makes no structural change to the immediate child list.
  let child: ChildNode | null = node.firstChild;
  while (child) {
    const next: ChildNode | null = child.nextSibling;
    if (child.nodeType === 8 /* COMMENT */ || child.nodeType === 7 /* PI */) {
      node.removeChild(child);
      child = next;
      continue;
    }
    if (child.nodeType === 1 /* ELEMENT */) {
      const el = child as Element;
      const tag = el.localName;
      if (DROP_WITH_CONTENT.has(tag)) {
        el.remove();
        child = next;
        continue;
      }
      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap in place; re-scan from the first promoted child.
        const first = el.firstChild ?? next;
        sanitizeElement(el);
        child = first;
        continue;
      }
      sanitizeElement(el); // attribute scrub
      sanitizeChildren(el);
    }
    child = next;
  }
}

/**
 * Sanitize untrusted HTML and wrap it as a renderable TemplateResult.
 *
 * NOTE: the result carries a live DocumentFragment — render it ONCE (lit-html
 * adopts the fragment's children into the host). Call `richText()` again for
 * each render rather than caching the result.
 */
export function richText(untrustedHtml: string): TemplateResult {
  if (DEV) {
    const site =
      new Error().stack?.split("\n")[2]?.trim() ?? "(unknown call site)";
    console.debug(
      `[web-kit] richText sink: content#${fnv1a(untrustedHtml)} at ${site}`
    );
  }

  const tpl = document.createElement("template");
  tpl.innerHTML = untrustedHtml; // inert: template content never executes scripts
  sanitizeChildren(tpl.content);
  return html`${tpl.content}`;
}
